import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const Section = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const section = document.getElementById("survey-section");
      if (section) {
        const rect = section.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        if (rect.top < windowHeight - 100 && rect.bottom > 0) {
          setVisible(true);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="w-full px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
      
      {/* 3 Top Cards Grid - Premium Fluid Height Overhaul */}
      <div id="survey-section" className="grid grid-cols-1 gap-8 mb-20 sm:grid-cols-2 lg:grid-cols-3">
        
        {/* Card 1 */}
        <div
          className={`w-full rounded-3xl text-white flex flex-col items-center justify-between p-8 min-h-[340px]
            bg-gradient-to-br from-blue-600 to-indigo-900 shadow-xl transform transition-all duration-500 hover:scale-[1.03] hover:shadow-2xl ${
              visible ? "animate-bounceIn opacity-100" : "opacity-0"
            }`}
        >
          <div className="flex flex-col items-center my-auto">
            <h2 className="mb-4 text-2xl font-bold tracking-tight text-center">Accurate Surveys</h2>
            <p className="text-sm leading-relaxed text-center text-blue-100">
              Precision you can rely on for every property boundary. Secure your investments with absolute geometric certainty.
            </p>
          </div>
        </div>

        {/* Card 2 */}
        <div
          className={`w-full rounded-3xl text-white flex flex-col items-center justify-between p-8 min-h-[340px]
            bg-gradient-to-br from-green-600 to-emerald-900 shadow-xl transform transition-all duration-500 hover:scale-[1.03] hover:shadow-2xl delay-200 ${
              visible ? "animate-revealUp opacity-100" : "opacity-0"
            }`}
        >
          <div className="flex flex-col items-center my-auto">
            <h2 className="mb-4 text-2xl font-bold tracking-tight text-center">Trusted Expertise</h2>
            <p className="text-sm leading-relaxed text-center text-emerald-100">
              Professional land surveying backed by years of experience and top-tier modern satellite GNSS/RTK technology.
            </p>
          </div>
        </div>

        {/* Card 3 */}
        <div
          className={`w-full rounded-3xl text-white flex flex-col items-center justify-between p-8 min-h-[340px]
            bg-gradient-to-br from-purple-600 to-pink-800 shadow-xl transform transition-all duration-500 hover:scale-[1.03] hover:shadow-2xl delay-500 ${
              visible ? "animate-bounceIn opacity-100" : "opacity-0"
            }`}
        >
          <div className="flex flex-col items-center my-auto">
            <h2 className="mb-4 text-2xl font-bold tracking-tight text-center">Clear Reports</h2>
            <p className="text-sm leading-relaxed text-center text-purple-100">
              Reliable information, clear boundary blueprints, and mapping legal frameworks to help you make the best land decisions.
            </p>
          </div>
        </div>
      </div>

      {/* Services Rows - Clean Asymmetric Layout Mapping */}
      <div className="mt-16 space-y-20">
        
        {/* Row 1: Image 4 */}
        <div className="flex flex-col items-center justify-between gap-10 lg:flex-row">
          <div className="w-full lg:w-5/12">
            <img
              src="/images/image4.webp"
              alt="Boundary Splitting"
              className="w-full h-[300px] object-cover rounded-2xl shadow-lg border border-slate-100"
            />
          </div>
          <div className="w-full lg:w-7/12">
            <h3 className="mb-3 text-2xl font-bold tracking-tight text-slate-800">Precision Boundary Line Cutting & Splitting</h3>
            <p className="leading-relaxed text-slate-600">
              Are you looking to divide inherited properties or sell a specific piece of land? Our professional team accurately marks out sub-divisions, resolving neighborhood space conflicts before they begin. We physically place permanent boundary beacons that match your property maps.
            </p>
            <Link to="/login" className="inline-flex items-center gap-1 mt-4 text-sm font-bold text-blue-600 transition-colors hover:text-blue-700">
              Request Boundary Allocation <span>&rarr;</span>
            </Link>
          </div>
        </div>

        {/* Row 2: Image 8 */}
        <div className="flex flex-col items-center justify-between gap-10 lg:flex-row-reverse">
          <div className="w-full lg:w-5/12">
            <img
              src="/images/image8.jpg"
              alt="Topographic Survey"
              className="w-full h-[300px] object-cover rounded-2xl shadow-lg border border-slate-100"
            />
          </div>
          <div className="w-full lg:w-7/12">
            <h3 className="mb-3 text-2xl font-bold tracking-tight text-slate-800">Comprehensive Topographic Engineering Layouts</h3>
            <p className="leading-relaxed text-slate-600">
              Before engineers or architects lay down concrete slabs, you need to understand the true slope of your soil. We deliver advanced 3D terrain mapping, showing contour variations, natural features, and underground utility structures. This blueprint ensures structural safety during construction.
            </p>
            <Link to="/Landing" className="inline-flex items-center gap-1 mt-4 text-sm font-bold text-green-600 transition-colors hover:text-green-700">
              Explore Engineering Mapping <span>&rarr;</span>
            </Link>
          </div>
        </div>

        {/* Row 3: Image 9 */}
        <div className="flex flex-col items-center justify-between gap-10 lg:flex-row">
          <div className="w-full lg:w-5/12">
            <img
              src="/images/image9.jpg"
              alt="Cadastral Documents"
              className="w-full h-[300px] object-cover rounded-2xl shadow-lg border border-slate-100"
            />
          </div>
          <div className="w-full lg:w-7/12">
            <h3 className="mb-3 text-2xl font-bold tracking-tight text-slate-800">Cadastral Registry Validation & Deed Services</h3>
            <p className="leading-relaxed text-slate-600">
              Make your land 100% legal. We process spatial surveying documents required by city development boards and the land registry office. We take your exact coordinates and turn them into validated, government-approved cadastral layout maps to secure your legal ownership title.
            </p>
            <Link to="/regist" className="inline-flex items-center gap-1 mt-4 text-sm font-bold text-purple-600 transition-colors hover:text-purple-700">
              Apply For Registration Support <span>&rarr;</span>
            </Link>
          </div>
        </div>

        {/* Row 4: Image 10 */}
        <div className="flex flex-col items-center justify-between gap-10 lg:flex-row-reverse">
          <div className="w-full lg:w-5/12">
            <img
              src="/images/image10.jpg"
              alt="Construction Staking"
              className="w-full h-[300px] object-cover rounded-2xl shadow-lg border border-slate-100"
            />
          </div>
          <div className="w-full lg:w-7/12">
            <h3 className="mb-3 text-2xl font-bold tracking-tight text-slate-800">Commercial Construction & Foundation Staking</h3>
            <p className="leading-relaxed text-slate-600">
              Keep your building project aligned precisely with approved floor plans. Our master surveyors move structural reference markers directly from paper blueprints onto the dirt. This precision layout staging eliminates configuration mistakes and safeguards against setbacks during high-value building processes.
            </p>
            <Link to="/login" className="inline-flex items-center gap-1 mt-4 text-sm font-bold transition-colors text-amber-600 hover:text-amber-700">
              Book Layout Staking <span>&rarr;</span>
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Section;